import { Bar, Line } from 'react-chartjs-2'
import { MetricCard } from 'components/FbUI/MetricCard'
import { PillTabs } from 'components/FbUI/PillTabs'
import { formatInteger, formatUsd } from 'utils/formatting'
import {
  format,
  startOfQuarter,
  endOfQuarter,
  endOfYear,
  startOfMonth,
  endOfMonth,
} from 'date-fns'
import { useGetRebateDashboardStats } from './tradespend_api'
import { Spinner } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import {
  PiChartBar,
  PiChartLine,
  PiChartPie,
  PiCurrencyDollar,
  PiPercent,
  PiTrendUp,
  PiUsersThree,
} from 'react-icons/pi'
import { FbDropdown } from 'components/FbUI/FbDropdown'
import { cn } from 'components/UI/cn'
import { DateRangeSelect } from './DateRangeSelect'
import { RebateClaimsTable } from './RebateClaimsTable'
import { Link } from 'react-router-dom'
import FbButton from 'components/FbUI/FbButton'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
)

// Chart color palette
const CHART_COLORS = [
  '#10B981', // emerald/green (primary)
  '#4F46E5', // indigo
  '#F59E0B', // amber
  '#EF4444', // red
  '#8B5CF6', // purple
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#F97316', // orange
  '#14B8A6', // teal
  '#6366F1', // violet
]

interface FormValues {
  showValue: 'claims' | 'amount'
  dateRange: {
    type: 'quarter' | 'half' | 'custom'
    quarter?: string
    half?: string
    year?: string
    dateRange?: {
      from: Date
      to: Date
    }
  }
  showAllClaims: 'all' | 'select'
  chartType: 'stacked' | 'line'
}

interface MonthlyTrendData {
  month: string
  aggregate?: { count: number; value: number }
  [key: string]: string | { count: number; value: number } | undefined
}

export function RebateDashboard() {
  // Get current quarter (1-4)
  const now = new Date()
  const currentQuarter = Math.floor(now.getMonth() / 3) + 1

  const { control, watch, setValue } = useForm<FormValues>({
    defaultValues: {
      showValue: 'claims',
      dateRange: {
        type: 'quarter',
        quarter: currentQuarter.toString(),
        year: now.getFullYear().toString(),
      },
      showAllClaims: 'all',
      chartType: 'stacked',
    },
  })

  const showValue = watch('showValue')
  const dateRange = watch('dateRange')
  const showAllClaims = watch('showAllClaims')
  const chartType = watch('chartType')

  // Get date range based on selection
  const getDateRange = () => {
    const currentYear = parseInt(
      dateRange.year || new Date().getFullYear().toString()
    )

    if (dateRange.type === 'quarter' && dateRange.quarter) {
      const quarter = parseInt(dateRange.quarter)
      const start = startOfQuarter(new Date(currentYear, (quarter - 1) * 3, 1))
      const end = endOfQuarter(start)
      return { start, end }
    }

    if (dateRange.type === 'half' && dateRange.half) {
      const half = parseInt(dateRange.half)
      const start = new Date(currentYear, (half - 1) * 6, 1)
      const end =
        half === 1
          ? new Date(currentYear, 5, 30)
          : endOfYear(new Date(currentYear, 11, 31))
      return { start, end }
    }

    if (
      dateRange.type === 'custom' &&
      dateRange.dateRange?.from &&
      dateRange.dateRange?.to
    ) {
      return {
        start: dateRange.dateRange.from,
        end: dateRange.dateRange.to,
      }
    }

    // Default to current quarter if no valid selection
    const start = startOfQuarter(new Date())
    const end = endOfQuarter(start)
    return { start, end }
  }

  const { start, end } = getDateRange()
  const { data: dashboardStats, isLoading } = useGetRebateDashboardStats(
    format(start, 'yyyy-MM-dd'),
    format(end, 'yyyy-MM-dd')
  )

  if (isLoading || !dashboardStats) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner animation="border" />
      </div>
    )
  }

  // Filter data for selected date range
  const filteredData = dashboardStats.monthly_trends.data.filter(
    (d: MonthlyTrendData) => {
      // Parse date in UTC to avoid timezone issues
      const [year, month] = d.month.split('-')
      const date = new Date(parseInt(year), parseInt(month) - 1, 1)
      return date >= startOfMonth(start) && date <= endOfMonth(end)
    }
  )

  // Compute derived values
  const isShowingValue = showValue === 'amount'
  const isShowingAllClaims = showAllClaims === 'all'

  // Filter offers for chart
  const activeOffers = isShowingAllClaims
    ? dashboardStats.monthly_trends.offers
    : dashboardStats.monthly_trends.offers

  const ChartComponent = chartType === 'stacked' ? Bar : Line

  return (
    <div className="w-full px-4 space-y-6 min-h-full">
      <div className="mx-auto">
        {/* Global Controls */}
        <div className="flex justify-between items-center mb-6">
          <DateRangeSelect
            control={control}
            name="dateRange"
            setValue={setValue}
          />
          <PillTabs<'claims' | 'amount'>
            aria-label="Display data by"
            idPrefix="rebate-display-toggle"
            activeKey={showValue}
            onChange={(value) => setValue('showValue', value)}
            size="lg"
            tabs={[
              {
                key: 'claims',
                title: (
                  <span className="inline-flex items-center gap-2 leading-none">
                    <PiChartPie size={18} className="shrink-0" />
                    <span className="leading-none">Claims</span>
                  </span>
                ),
              },
              {
                key: 'amount',
                title: (
                  <span className="inline-flex items-center gap-2 leading-none">
                    <PiCurrencyDollar size={18} className="shrink-0" />
                    <span className="leading-none">Amount</span>
                  </span>
                ),
              },
            ]}
          />
        </div>
        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 w-full mb-4">
          <MetricCard
            label="Foodservice Chains"
            value={formatInteger(dashboardStats.foodservice_chains)}
            tooltipContent="Total number of unique foodservice chains that have submitted claims"
            icon={<PiUsersThree size={18} />}
          />
          <MetricCard
            label="Claims Received"
            value={formatInteger(dashboardStats.claims_received)}
            tooltipContent="Total number of rebate claims received across all offers"
            icon={<PiTrendUp size={18} />}
          />
          <MetricCard
            label="Rebate Value Processed"
            value={formatUsd(dashboardStats.rebate_value_processed)}
            tooltipContent="Total monetary value of all processed rebate claims"
            icon={<PiCurrencyDollar size={18} />}
          />
          <MetricCard
            label="Redemption Rate"
            value={`${dashboardStats.redemption_rate}%`}
            tooltipContent="Percentage of enrollments with at least one validated/paid claim"
            icon={<PiPercent size={18} />}
          />
        </div>

        {/* Claims by Offer */}
        <div
          className="w-full bg-white p-6 rounded-lg border border-neutral-200 mb-8"
          style={{
            boxShadow:
              '0px 1px 3px 0px rgba(16, 24, 40, 0.08), 0px 1px 2px -1px rgba(16, 24, 40, 0.04)',
          }}
        >
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold">Claims by Rebate Offer</h3>
          </div>
          <Bar
            data={{
              labels: dashboardStats.claims_by_offer.map((o) => o.offer_name),
              datasets: [
                {
                  data: dashboardStats.claims_by_offer.map((o) =>
                    isShowingValue ? o.value : o.count
                  ),
                  backgroundColor: 'rgba(16, 185, 129, 0.2)', // emerald with opacity
                  borderColor: '#10B981', // emerald
                  borderWidth: 1,
                  borderRadius: 4,
                },
              ],
            }}
            options={{
              indexAxis: 'y',
              plugins: {
                legend: {
                  display: false,
                },
                tooltip: {
                  callbacks: {
                    label: (context) => {
                      const offer =
                        dashboardStats.claims_by_offer[context.dataIndex]
                      if (isShowingValue) {
                        return `Value: ${formatUsd(context.parsed.x ?? undefined)}`
                      }
                      return `${context.parsed.x} claims (${formatUsd(
                        offer.value
                      )})`
                    },
                  },
                },
              },
              scales: {
                x: {
                  beginAtZero: true,
                  ticks: {
                    precision: 0,
                    callback: (value) =>
                      isShowingValue
                        ? formatUsd(value as number)
                        : formatInteger(value as number),
                  },
                },
              },
            }}
          />
        </div>

        {/* Monthly Claims Visualization */}
        <div
          className="w-full bg-white rounded-lg border border-neutral-200 p-6 mb-8"
          style={{
            boxShadow:
              '0px 1px 3px 0px rgba(16, 24, 40, 0.08), 0px 1px 2px -1px rgba(16, 24, 40, 0.04)',
          }}
        >
          {/* Header — title left, data-view dropdown + chart-type icon toggle right */}
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-lg font-semibold">Monthly Claims</h3>
            <div className="flex items-center gap-3">
              <FbDropdown
                aria-label="Data view"
                label={`View: ${showAllClaims === 'all' ? 'All Claims' : 'By Offer'}`}
                items={[
                  { key: 'all', label: 'All Claims' },
                  { key: 'select', label: 'By Offer' },
                ]}
                selectedKey={showAllClaims}
                onAction={(key) =>
                  setValue('showAllClaims', key as 'all' | 'select')
                }
                placement="bottom end"
              />
              <div
                role="radiogroup"
                aria-label="Chart type"
                className="inline-flex h-10 items-center gap-1 rounded-lg bg-neutral-100 p-1"
              >
                <button
                  type="button"
                  role="radio"
                  aria-checked={chartType === 'stacked'}
                  title="Stacked"
                  aria-label="Stacked"
                  onClick={() => setValue('chartType', 'stacked')}
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-md transition-colors',
                    chartType === 'stacked'
                      ? 'bg-white text-neutral-800 shadow-tab-active'
                      : 'text-neutral-500 hover:text-neutral-800'
                  )}
                >
                  <PiChartBar size={18} />
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={chartType === 'line'}
                  title="Line"
                  aria-label="Line"
                  onClick={() => setValue('chartType', 'line')}
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-md transition-colors',
                    chartType === 'line'
                      ? 'bg-white text-neutral-800 shadow-tab-active'
                      : 'text-neutral-500 hover:text-neutral-800'
                  )}
                >
                  <PiChartLine size={18} />
                </button>
              </div>
            </div>
          </div>

          {/* Chart */}
          <div className="h-96 mt-6">
            <ChartComponent
              data={{
                labels: filteredData.map((d: MonthlyTrendData) => {
                  const [year, month] = d.month.split('-')
                  const date = new Date(parseInt(year), parseInt(month) - 1, 1)
                  return format(date, 'MMM yyyy')
                }),
                datasets: isShowingAllClaims
                  ? [
                      {
                        label: 'All Claims',
                        data: filteredData.map((d: MonthlyTrendData) => {
                          if (!d.aggregate) return 0
                          return isShowingValue
                            ? d.aggregate.value
                            : d.aggregate.count
                        }),
                        backgroundColor: CHART_COLORS[0],
                        borderColor: CHART_COLORS[0],
                        borderWidth: chartType === 'line' ? 2 : 1,
                        tension: chartType === 'line' ? 0.4 : 0,
                        pointRadius: chartType === 'line' ? 4 : 0,
                      },
                    ]
                  : activeOffers.map((offer: string, index: number) => ({
                      label: offer,
                      data: filteredData.map((d: MonthlyTrendData) => {
                        const offerData = d[offer] as
                          | { count: number; value: number }
                          | undefined
                        if (!offerData) return 0
                        return isShowingValue
                          ? offerData.value
                          : offerData.count
                      }),
                      backgroundColor:
                        CHART_COLORS[index % CHART_COLORS.length],
                      borderColor: CHART_COLORS[index % CHART_COLORS.length],
                      borderWidth: chartType === 'line' ? 2 : 1,
                      tension: chartType === 'line' ? 0.4 : 0,
                      pointRadius: chartType === 'line' ? 4 : 0,
                      stack: chartType === 'stacked' ? 'stack0' : undefined,
                    })),
              }}
              options={{
                maintainAspectRatio: false,
                responsive: true,
                interaction: {
                  mode: 'index' as const,
                  intersect: false,
                },
                scales: {
                  x: {
                    stacked: chartType === 'stacked',
                  },
                  y: {
                    stacked: chartType === 'stacked',
                    beginAtZero: true,
                    ticks: {
                      callback: (value) =>
                        isShowingValue
                          ? formatUsd(value as number)
                          : formatInteger(value as number),
                    },
                  },
                },
                plugins: {
                  tooltip: {
                    callbacks: {
                      label: (context) => {
                        const value = context.parsed.y ?? undefined
                        return isShowingValue
                          ? `${context.dataset.label}: ${formatUsd(value)}`
                          : `${context.dataset.label}: ${formatInteger(
                              value
                            )} claims`
                      },
                    },
                  },
                  legend: {
                    position: 'bottom' as const,
                    labels: {
                      boxWidth: 12,
                      padding: 15,
                    },
                  },
                },
              }}
            />
          </div>
        </div>

        {/* Recent Claims */}
        <TableLinkStyleProvider variant="plain">
          <div
            className="w-full overflow-hidden rounded-lg border border-neutral-200 bg-white"
            style={{
              boxShadow:
                '0px 1px 3px 0px rgba(16, 24, 40, 0.08), 0px 1px 2px -1px rgba(16, 24, 40, 0.04)',
            }}
          >
            <div className="flex flex-col gap-1 px-6 pt-6 pb-4">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-lg font-semibold text-fg">Recent Claims</h3>
                <Link to="/rebates/claims">
                  <FbButton
                    size="md"
                    className="!bg-brand-bright !text-white hover:!bg-brand-active"
                  >
                    View All Claims
                  </FbButton>
                </Link>
              </div>
              <div className="text-sm text-fg-secondary">
                Showing the 10 most recent claims for your rebate offers
              </div>
            </div>
            <div className="min-h-[600px]">
              <RebateClaimsTable
                limit={10}
                isPaginationEnabled={false}
                tableKey="dashboard-recent-claims"
                hideFilters={true}
                hideColumnSelector={true}
                disableSorting={true}
              />
            </div>
          </div>
        </TableLinkStyleProvider>
      </div>
    </div>
  )
}
